(() => {
  'use strict';

  const DIMENSION_KEYS = ['hydratation', 'eclat', 'texture', 'rougeurs', 'pores', 'uniformite'];
  const CONFIDENCE_KEYS = {
    faible: 'low',
    low: 'low',
    moyenne: 'medium',
    medium: 'medium',
    elevee: 'high',
    élevée: 'high',
    high: 'high',
  };
  const PHOTO_KEYS = ['front', 'left', 'right'];

  class VelvetSkinAIUserError extends Error {}

  class VelvetSkinAI {
    constructor(root) {
      if (root.dataset.vsaReady === 'true') return;
      root.dataset.vsaReady = 'true';
      this.root = root;
      this.dialog = root.querySelector('[data-vsa-dialog]');
      this.presentation = root.dataset.presentation || 'modal';
      this.locale = root.dataset.locale || window.Shopify?.locale || document.documentElement.lang || 'fr';
      this.language = String(this.locale).toLowerCase().split(/[-_]/)[0];
      this.i18n = this.readTranslations();
      this.rootPath = window.Shopify?.routes?.root || '/';
      this.photos = new Map();
      this.previewUrls = new Map();
      this.routine = [];
      this.responseLocale = '';
      this.loadingTimer = null;
      this.bind();
      this.setStep('consent');
    }

    readTranslations() {
      const source = this.root.querySelector('[data-vsa-translations]');
      if (!source) return {};
      try { return JSON.parse(source.textContent || '{}'); }
      catch { return {}; }
    }

    t(path, fallback = '') {
      const value = String(path).split('.').reduce((current, key) => current?.[key], this.i18n);
      return typeof value === 'string' && value ? value : fallback;
    }

    localizedText(source, field, inheritedLocale = '') {
      if (!source || typeof source !== 'object') return '';
      const normalizedLocale = String(this.locale || '').replace(/_/g, '-');
      const localeKeys = [...new Set([
        normalizedLocale,
        normalizedLocale.toLowerCase(),
        normalizedLocale.replace(/-/g, '_'),
        normalizedLocale.toLowerCase().replace(/-/g, '_'),
        this.language,
      ].filter(Boolean))];
      const localizedCollections = [source[`${field}ByLocale`], source[`${field}Translations`]];
      for (const collection of localizedCollections) {
        if (!collection || typeof collection !== 'object') continue;
        for (const localeKey of localeKeys) {
          const exactValue = collection[localeKey];
          if (typeof exactValue === 'string' && exactValue) return exactValue;
          const matchedKey = Object.keys(collection).find((key) => key.toLowerCase().replace(/_/g, '-') === localeKey.toLowerCase().replace(/_/g, '-'));
          if (matchedKey && typeof collection[matchedKey] === 'string' && collection[matchedKey]) return collection[matchedKey];
        }
      }

      const titleCaseSuffix = normalizedLocale
        .split('-')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join('');
      const regionCaseSuffix = normalizedLocale
        .split('-')
        .filter(Boolean)
        .map((part, index) => index === 0
          ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
          : part.toUpperCase())
        .join('');
      const languageSuffix = this.language.charAt(0).toUpperCase() + this.language.slice(1);
      const fieldKeys = [...new Set([
        `${field}${titleCaseSuffix}`,
        `${field}${regionCaseSuffix}`,
        `${field}${languageSuffix}`,
        `${field}_${normalizedLocale}`,
        `${field}_${this.language}`,
      ])];
      for (const fieldKey of fieldKeys) {
        if (typeof source[fieldKey] === 'string' && source[fieldKey]) return source[fieldKey];
      }
      const declaredLocale = source.locale || source.language || source.languageCode || source.responseLocale || inheritedLocale;
      const declaredLanguage = String(declaredLocale || '').toLowerCase().split(/[-_]/)[0];
      const bareFieldMatchesLocale = this.language === 'fr' || (declaredLanguage && declaredLanguage === this.language);
      return bareFieldMatchesLocale && typeof source[field] === 'string' ? source[field] : '';
    }

    bind() {
      this.root.querySelector('[data-vsa-open]')?.addEventListener('click', () => this.open());
      this.root.querySelector('[data-vsa-close]')?.addEventListener('click', () => this.close());
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog && this.presentation === 'modal') this.close();
      });

      const consent = this.root.querySelector('[data-vsa-consent]');
      const start = this.root.querySelector('[data-vsa-start]');
      consent?.addEventListener('change', () => { start.disabled = !consent.checked; });
      start?.addEventListener('click', () => this.setStep('photos'));
      this.root.querySelector('[data-vsa-back]')?.addEventListener('click', () => this.setStep('consent'));
      this.root.querySelector('[data-vsa-analyze]')?.addEventListener('click', () => this.analyze());
      this.root.querySelector('[data-vsa-restart]')?.addEventListener('click', () => this.restart());

      for (const key of PHOTO_KEYS) {
        this.root.querySelector(`[data-vsa-photo="${key}"]`)?.addEventListener('change', (event) => this.preparePhoto(key, event.target.files?.[0]));
      }

      this.root.querySelector('[data-vsa-routine]')?.addEventListener('change', () => this.updateTotal());
      this.root.querySelector('[data-vsa-routine]')?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-vsa-add-one]');
        if (button) this.addSingle(button.dataset.vsaAddOne, button);
      });
      this.root.querySelector('[data-vsa-add-all]')?.addEventListener('click', (event) => this.addAll(event.currentTarget));
    }

    open() {
      this.hideError();
      if (typeof this.dialog?.showModal === 'function') this.dialog.showModal();
      else this.dialog?.setAttribute('open', '');
    }

    close() {
      if (this.presentation !== 'modal') return;
      if (typeof this.dialog?.close === 'function') this.dialog.close();
      else this.dialog?.removeAttribute('open');
    }

    setStep(name) {
      const index = { consent: 0, photos: 1, loading: 1, results: 2 }[name] ?? 0;
      this.root.querySelectorAll('[data-vsa-step]').forEach((step) => {
        const active = step.dataset.vsaStep === name;
        step.hidden = !active;
        step.classList.toggle('is-active', active);
      });
      const progress = this.root.querySelector('[data-vsa-progress]');
      progress?.querySelectorAll('span').forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex === index);
        item.classList.toggle('is-done', itemIndex < index);
      });
      progress?.querySelectorAll('i').forEach((line, lineIndex) => line.classList.toggle('is-done', lineIndex < index));
      this.hideError();
      this.dialog?.scrollTo({ top: 0, behavior: 'smooth' });
    }

    showError(message) {
      const error = this.root.querySelector('[data-vsa-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
      error.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    hideError() {
      const error = this.root.querySelector('[data-vsa-error]');
      if (error) { error.hidden = true; error.textContent = ''; }
    }

    async preparePhoto(key, file) {
      this.hideError();
      if (!file) return;
      if (file.size > 16 * 1024 * 1024) {
        this.showError(this.t('errors.photoTooLarge'));
        return;
      }
      try {
        const prepared = await this.resizeImage(file);
        this.photos.set(key, prepared);
        const oldUrl = this.previewUrls.get(key);
        if (oldUrl) URL.revokeObjectURL(oldUrl);
        const previewUrl = URL.createObjectURL(prepared);
        this.previewUrls.set(key, previewUrl);
        const preview = this.root.querySelector(`[data-vsa-preview="${key}"]`);
        if (preview) { preview.src = previewUrl; preview.hidden = false; }
        this.root.querySelector(`[data-vsa-photo-card="${key}"]`)?.classList.add('has-photo');
        const status = this.root.querySelector(`[data-vsa-photo-status="${key}"]`);
        if (status) status.textContent = this.t('photos.modify');
      } catch {
        this.showError(this.t('errors.unreadableImage'));
      }
      this.root.querySelector('[data-vsa-analyze]').disabled = PHOTO_KEYS.some((photoKey) => !this.photos.has(photoKey));
    }

    async resizeImage(file) {
      let source;
      let revokeSource = null;
      try {
        source = await createImageBitmap(file, { imageOrientation: 'from-image' });
      } catch {
        const url = URL.createObjectURL(file);
        revokeSource = url;
        source = await new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = reject;
          image.src = url;
        });
      }

      const width = source.width || source.naturalWidth;
      const height = source.height || source.naturalHeight;
      const scale = Math.min(1, 1600 / Math.max(width, height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      canvas.getContext('2d', { alpha: false }).drawImage(source, 0, 0, canvas.width, canvas.height);
      source.close?.();
      if (revokeSource) URL.revokeObjectURL(revokeSource);
      const blob = await new Promise((resolve, reject) => canvas.toBlob((value) => value ? resolve(value) : reject(new Error('conversion')), 'image/jpeg', .86));
      canvas.width = 1;
      canvas.height = 1;
      return blob;
    }

    startLoadingText() {
      const target = this.root.querySelector('[data-vsa-loading-text]');
      const messages = Array.isArray(this.i18n.loading) ? this.i18n.loading.filter(Boolean) : [];
      if (!messages.length) return;
      let index = 0;
      if (target) target.textContent = messages[0];
      this.loadingTimer = window.setInterval(() => {
        index = (index + 1) % messages.length;
        if (target) target.textContent = messages[index];
      }, 2800);
    }

    stopLoadingText() {
      window.clearInterval(this.loadingTimer);
      this.loadingTimer = null;
    }

    async analyze() {
      if (PHOTO_KEYS.some((key) => !this.photos.has(key))) return;
      const consent = this.root.querySelector('[data-vsa-consent]');
      if (!consent?.checked) {
        this.setStep('consent');
        this.showError(this.t('errors.consentRequired'));
        return;
      }
      this.setStep('loading');
      this.startLoadingText();
      const formData = new FormData();
      PHOTO_KEYS.forEach((key) => formData.append(key, this.photos.get(key), `${key}.jpg`));
      formData.append('consent', 'true');
      formData.append('consentVersion', '2026-09-05');
      formData.append('locale', this.locale);

      try {
        const response = await fetch(this.root.dataset.apiEndpoint || '/apps/velvet-skin-ai/analyze', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new VelvetSkinAIUserError(this.localizedText(payload, 'message') || this.t('errors.analysisFailed'));
        this.renderResults(payload);
        this.releasePhotos();
        this.setStep('results');
      } catch (error) {
        this.setStep('photos');
        this.showError(error instanceof VelvetSkinAIUserError ? error.message : this.t('errors.analysisUnavailable'));
      } finally {
        this.stopLoadingText();
      }
    }

    releasePhotos() {
      this.photos.clear();
      for (const url of this.previewUrls.values()) URL.revokeObjectURL(url);
      this.previewUrls.clear();
      PHOTO_KEYS.forEach((key) => {
        const input = this.root.querySelector(`[data-vsa-photo="${key}"]`);
        const preview = this.root.querySelector(`[data-vsa-preview="${key}"]`);
        if (input) input.value = '';
        if (preview) { preview.removeAttribute('src'); preview.hidden = true; }
        this.root.querySelector(`[data-vsa-photo-card="${key}"]`)?.classList.remove('has-photo');
        const status = this.root.querySelector(`[data-vsa-photo-status="${key}"]`);
        if (status) status.textContent = this.t('photos.add');
      });
      const analyzeButton = this.root.querySelector('[data-vsa-analyze]');
      if (analyzeButton) analyzeButton.disabled = true;
    }

    renderResults(payload) {
      const analysis = payload.analysis || {};
      this.responseLocale = payload.locale || payload.language || payload.languageCode || payload.responseLocale || '';
      const score = Number(analysis.overallScore) || 0;
      const scoreRing = this.root.querySelector('[data-vsa-score-ring]');
      if (scoreRing) scoreRing.style.setProperty('--score', Math.max(0, Math.min(100, score)));
      const scoreText = this.root.querySelector('[data-vsa-score]');
      if (scoreText) scoreText.textContent = String(score);
      const summary = this.root.querySelector('[data-vsa-summary]');
      if (summary) summary.textContent = this.localizedText(analysis, 'summary', this.responseLocale) || this.t('results.summaryFallback');
      const confidence = this.root.querySelector('[data-vsa-confidence]');
      const confidenceKey = CONFIDENCE_KEYS[String(analysis.confidence || '').toLowerCase()] || 'low';
      if (confidence) confidence.textContent = this.t(`confidence.${confidenceKey}`);

      const dimensions = this.root.querySelector('[data-vsa-dimensions]');
      dimensions.replaceChildren();
      DIMENSION_KEYS.forEach((key) => {
        const value = Math.max(0, Math.min(100, Number(analysis.dimensions?.[key]) || 0));
        const item = document.createElement('div');
        item.className = 'velvet-skin-ai__dimension';
        const name = document.createElement('span'); name.textContent = this.t(`dimensions.${key}`, key);
        const number = document.createElement('strong'); number.textContent = `${value} / 100`;
        const bar = document.createElement('i'); bar.style.setProperty('--dimension', value);
        item.append(name, number, bar);
        dimensions.append(item);
      });

      const priorities = this.root.querySelector('[data-vsa-priorities]');
      priorities.replaceChildren();
      (analysis.priorities || []).forEach((key) => {
        const item = document.createElement('span');
        item.className = 'velvet-skin-ai__priority';
        item.textContent = this.t(`priorities.${key}`) || this.t(`dimensions.${key}`, key);
        priorities.append(item);
      });

      this.routine = Array.isArray(payload.routine) ? payload.routine.filter((item) => item.availableForSale && item.variantId) : [];
      const routineRoot = this.root.querySelector('[data-vsa-routine]');
      routineRoot.replaceChildren(...this.routine.map((product, index) => this.productCard(product, index)));
      const notice = this.root.querySelector('[data-vsa-catalog-notice]');
      const catalogNotice = this.localizedText(payload, 'catalogNotice');
      if (notice) { notice.textContent = catalogNotice; notice.hidden = !catalogNotice; }
      const footer = this.root.querySelector('[data-vsa-routine-footer]');
      if (footer) footer.hidden = this.routine.length === 0;
      const disclaimer = this.root.querySelector('[data-vsa-disclaimer]');
      if (disclaimer) disclaimer.textContent = this.localizedText(payload, 'disclaimer') || this.t('results.disclaimer');
      const cartStatus = this.root.querySelector('[data-vsa-cart-status]');
      if (cartStatus) cartStatus.textContent = '';
      this.updateTotal();
    }

    productCard(product, index) {
      const article = document.createElement('article');
      article.className = 'velvet-skin-ai__product';
      const checkWrap = document.createElement('label'); checkWrap.className = 'velvet-skin-ai__product-check';
      const checkbox = document.createElement('input'); checkbox.type = 'checkbox'; checkbox.checked = true; checkbox.dataset.vsaSelected = String(index); checkbox.setAttribute('aria-label', this.t('product.include').replace('__PRODUCT__', product.title)); checkWrap.append(checkbox);
      const media = document.createElement('div'); media.className = 'velvet-skin-ai__product-image';
      if (product.image) { const image = document.createElement('img'); image.src = product.image; image.alt = product.imageAlt || product.title; image.loading = 'lazy'; media.append(image); }
      else { const placeholder = document.createElement('span'); placeholder.textContent = 'V'; placeholder.setAttribute('aria-hidden', 'true'); media.append(placeholder); }
      const info = document.createElement('div');
      const step = document.createElement('p'); step.className = 'velvet-skin-ai__product-step'; step.textContent = this.localizedText(product, 'stepLabel', this.responseLocale) || this.t('product.care');
      const title = document.createElement('h4'); title.textContent = product.title;
      const vendor = document.createElement('span'); vendor.className = 'velvet-skin-ai__product-vendor'; vendor.textContent = product.vendor || 'Velvet Essences';
      const explanation = document.createElement('p'); explanation.className = 'velvet-skin-ai__product-explanation'; explanation.textContent = this.localizedText(product, 'explanation', this.responseLocale);
      info.append(step, title, vendor, explanation);
      const buy = document.createElement('div'); buy.className = 'velvet-skin-ai__product-buy';
      const price = document.createElement('strong'); price.textContent = this.formatPrice(product.price, product.currencyCode);
      const add = document.createElement('button'); add.type = 'button'; add.dataset.vsaAddOne = product.variantId; add.textContent = this.t('product.add');
      const link = document.createElement('a'); link.href = `${this.rootPath}products/${encodeURIComponent(product.handle)}`; link.textContent = this.t('product.view');
      buy.append(price, add, link);
      article.append(checkWrap, media, info, buy);
      return article;
    }

    selectedProducts() {
      return [...this.root.querySelectorAll('[data-vsa-selected]:checked')]
        .map((checkbox) => this.routine[Number(checkbox.dataset.vsaSelected)])
        .filter(Boolean);
    }

    updateTotal() {
      const products = this.selectedProducts();
      const currency = products[0]?.currencyCode || 'EUR';
      const total = products.reduce((sum, product) => sum + Number(product.price || 0), 0);
      const target = this.root.querySelector('[data-vsa-total]');
      if (target) target.textContent = this.formatPrice(total, currency);
      const button = this.root.querySelector('[data-vsa-add-all]');
      if (button) button.disabled = products.length === 0;
    }

    formatPrice(value, currency) {
      try { return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency: currency || 'EUR' }).format(Number(value)); }
      catch { return `${Number(value).toFixed(2)} ${currency || 'EUR'}`; }
    }

    async addItems(products, button) {
      if (!products.length) return;
      const original = button.textContent;
      button.disabled = true;
      button.textContent = this.t('cart.adding');
      const cartStatus = this.root.querySelector('[data-vsa-cart-status]');
      if (cartStatus) cartStatus.textContent = '';
      const rootPath = window.Shopify?.routes?.root || '/';
      try {
        const response = await fetch(`${rootPath}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: products.map((product) => ({ id: product.variantId, quantity: 1 })) }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new VelvetSkinAIUserError(this.localizedText(payload, 'description') || this.t('errors.addSelection'));
        button.textContent = products.length > 1 ? this.t('cart.routineAdded') : this.t('cart.itemAdded');
        if (cartStatus) {
          cartStatus.replaceChildren(document.createTextNode(`${this.t('cart.selectionInCart')} `));
          const link = document.createElement('a'); link.href = `${rootPath}cart`; link.textContent = this.t('cart.viewCart');
          cartStatus.append(link);
        }
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (error) {
        button.textContent = original;
        if (cartStatus) cartStatus.textContent = error instanceof VelvetSkinAIUserError ? error.message : this.t('errors.addProducts');
      } finally {
        window.setTimeout(() => { button.disabled = false; if (button.textContent.includes('✓')) button.textContent = original; }, 1600);
      }
    }

    addSingle(variantId, button) {
      const product = this.routine.find((item) => String(item.variantId) === String(variantId));
      if (product) this.addItems([product], button);
    }

    addAll(button) { this.addItems(this.selectedProducts(), button); }

    restart() {
      this.routine = [];
      this.responseLocale = '';
      this.releasePhotos();
      const consent = this.root.querySelector('[data-vsa-consent]');
      if (consent) consent.checked = false;
      const start = this.root.querySelector('[data-vsa-start]');
      if (start) start.disabled = true;
      this.setStep('consent');
    }
  }

  function initialize(container = document) {
    container.querySelectorAll('[data-velvet-skin-ai]').forEach((root) => new VelvetSkinAI(root));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
